import React, { useState, useEffect, useMemo } from 'react';
import {
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  CardActions,
  Button,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Box,
  TextField,
  Pagination,
  Chip,
  Stack,
  Paper,
  CircularProgress,
  Alert,
  // IconButton,
  // Tooltip,
} from '@mui/material';
import {
  Search as SearchIcon,
  // Sort as SortIcon,
  // FilterList as FilterIcon,
  // Download as DownloadIcon,
} from '@mui/icons-material';
import { Link as RouterLink } from 'react-router-dom';
import { styled } from '@mui/material/styles';
import { useIssues } from '../context/IssueContext';
import { IssueStatus } from '../types/issue';

// Стилизованные компоненты
const FilterSection = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(2),
  marginBottom: theme.spacing(3),
}));

const IssueCard = styled(Card)(({ theme }) => ({
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  transition: 'transform 0.2s',
  '&:hover': {
    transform: 'translateY(-4px)',
  },
}));

const Issues: React.FC = () => {
  const [year, setYear] = useState<string>('all');
  const [quarter, setQuarter] = useState<string>('all');
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [sortBy, setSortBy] = useState<string>('date');
  const [page, setPage] = useState<number>(1);
  // const [isDownloading, setIsDownloading] = useState<number | null>(null);
  const itemsPerPage = 6;

  // Используем контекст выпусков
  const { issues, loading, error, fetchIssues /*, incrementDownloads */ } = useIssues();

  // Загрузка выпусков при монтировании компонента
  useEffect(() => {
    fetchIssues();
  }, [fetchIssues]);

  const years = useMemo(() =>
    ['all', ...Array.from(new Set(issues.map(issue => issue.year.toString())))].sort((a, b) =>
      b === 'all' ? 1 : a === 'all' ? -1 : Number(b) - Number(a)
    ), [issues]
  );

  const quarters = [
    { value: 'all', label: 'Все кварталы' },
    { value: 'Q1', label: '1 квартал' },
    { value: 'Q2', label: '2 квартал' },
    { value: 'Q3', label: '3 квартал' },
    { value: 'Q4', label: '4 квартал' },
  ];

  // Мемоизация фильтрации и сортировки
  const filteredAndSortedIssues = useMemo(() => {
    let filtered = issues.filter(issue => issue.status === IssueStatus.PUBLISHED);

    if (year !== 'all') {
      filtered = filtered.filter(issue => issue.year.toString() === year);
    }

    if (quarter !== 'all') {
      const quarterMonths = {
        Q1: [1, 2, 3],
        Q2: [4, 5, 6],
        Q3: [7, 8, 9],
        Q4: [10, 11, 12],
      };
      filtered = filtered.filter(issue =>
        quarterMonths[quarter as keyof typeof quarterMonths].includes(issue.number)
      );
    }

    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(issue =>
        issue.title.toLowerCase().includes(query)
        // || issue.description?.toLowerCase().includes(query)
      );
    }

    return filtered.sort((a, b) => {
      switch (sortBy) {
        case 'date':
          return b.year - a.year || b.number - a.number;
        case 'articles':
          return b.articles.length - a.articles.length;
        // case 'downloads':
        //   return b.downloads - a.downloads;
        default:
          return 0;
      }
    });
  }, [issues, year, quarter, searchQuery, sortBy]);

  // Мемоизация пагинации
  const paginatedIssues = useMemo(() => {
    const startIndex = (page - 1) * itemsPerPage;
    return filteredAndSortedIssues.slice(startIndex, startIndex + itemsPerPage);
  }, [filteredAndSortedIssues, page]);

  const totalPages = Math.ceil(filteredAndSortedIssues.length / itemsPerPage);

  // const handleDownload = async (id: number) => {
  //   try {
  //     setIsDownloading(id);
  //     await incrementDownloads(id);
  //   } catch (error) {
  //     console.error('Ошибка при скачивании:', error);
  //   } finally {
  //     setIsDownloading(null);
  //   }
  // };

  if (loading) {
    return (
      <Container sx={{ py: 4, display: 'flex', justifyContent: 'center' }}>
        <CircularProgress />
      </Container>
    );
  }

  if (error) {
    return (
      <Container sx={{ py: 4 }}>
        <Alert severity="error">{error}</Alert>
      </Container>
    );
  }

  return (
    <Container sx={{ py: 4 }}>
      <Typography variant="h4" component="h1" gutterBottom>
        Выпуски журнала
      </Typography>

      <FilterSection elevation={1}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={6} md={3}>
            <FormControl fullWidth>
              <InputLabel id="year-select-label">Год</InputLabel>
              <Select
                labelId="year-select-label"
                value={year}
                label="Год"
                onChange={(e) => setYear(e.target.value)}
              >
                <MenuItem value="all">Все годы</MenuItem>
                {years.slice(1).map((y) => (
                  <MenuItem key={y} value={y}>
                    {y}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <FormControl fullWidth>
              <InputLabel id="quarter-select-label">Квартал</InputLabel>
              <Select
                labelId="quarter-select-label"
                value={quarter}
                label="Квартал"
                onChange={(e) => setQuarter(e.target.value)}
              >
                {quarters.map((q) => (
                  <MenuItem key={q.value} value={q.value}>
                    {q.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <FormControl fullWidth>
              <InputLabel id="sort-select-label">Сортировка</InputLabel>
              <Select
                labelId="sort-select-label"
                value={sortBy}
                label="Сортировка"
                onChange={(e) => setSortBy(e.target.value)}
              >
                <MenuItem value="date">По дате</MenuItem>
                <MenuItem value="articles">По количеству статей</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              fullWidth
              placeholder="Поиск..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              InputProps={{
                startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />,
              }}
            />
          </Grid>
        </Grid>
      </FilterSection>

      <Grid container spacing={4}>
        {paginatedIssues.map((issue) => (
          <Grid key={issue.id} item xs={12} sm={6} lg={4}>
            <IssueCard>
              <CardContent sx={{ flexGrow: 1 }}>
                <Typography variant="h6" gutterBottom>
                  {issue.title}
                </Typography>
                {/* <Typography variant="body2" color="text.secondary" paragraph>
                  {issue.description}
                </Typography> */}
                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                  {issue.topics?.map((topic, index) => (
                    <Chip key={index} label={topic} size="small" />
                  ))}
                </Stack>
              </CardContent>
              <CardActions sx={{ justifyContent: 'space-between', p: 2 }}>
                <Box>
                  <Typography variant="body2" color="text.secondary">
                    Статей: {issue.articles?.length || 0}
                  </Typography>
                  {/* <Typography variant="body2" color="text.secondary">
                    Скачиваний: {issue.downloads}
                  </Typography> */}
                </Box>
                <Box>
                  <Button
                    component={RouterLink}
                    to={`/issues/${issue.id}/${issue.title.toLowerCase().replace(/\s+/g, '-')}`}
                    variant="outlined"
                    size="small"
                    sx={{ mr: 1 }}
                  >
                    Подробнее
                  </Button>
                  {/* <Tooltip title="Скачать выпуск">
                    <IconButton
                      size="small"
                      onClick={() => handleDownload(issue.id)}
                      disabled={isDownloading === issue.id}
                    >
                      {isDownloading === issue.id ? (
                        <CircularProgress size={20} />
                      ) : (
                        <DownloadIcon />
                      )}
                    </IconButton>
                  </Tooltip> */}
                </Box>
              </CardActions>
            </IssueCard>
          </Grid>
        ))}
      </Grid>

      {totalPages > 1 && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
          <Pagination
            count={totalPages}
            page={page}
            onChange={(_, value) => setPage(value)}
            color="primary"
          />
        </Box>
      )}
    </Container>
  );
};

export default Issues; 